import { useEffect, useState, useSyncExternalStore } from 'react';

let required = false;
let minimum: string | undefined;
const listeners = new Set<() => void>();

export function requireAppUpgrade(detail: { minAppVersion?: string; message?: string }): void {
  required = true;
  minimum = detail.minAppVersion;
  listeners.forEach((listener) => listener());
  window.dispatchEvent(new CustomEvent('app:upgrade-required', { detail }));
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isAppUpgradeRequired(): boolean {
  return required;
}

export function useAppUpgradeRequired(): boolean {
  return useSyncExternalStore(subscribe, isAppUpgradeRequired, () => false);
}

export function useAppUpgradeResolution(
  resolve: (minimum: string) => Promise<{
    status: 'no-update' | 'staged' | 'reloading' | 'store-required';
  }>
) {
  const required = useAppUpgradeRequired();
  const minAppVersion = useSyncExternalStore(
    subscribe,
    () => minimum,
    () => undefined
  );
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    minimum: string | undefined;
    attempt: number;
    status: 'checking' | 'store' | 'error';
  }>();

  useEffect(() => {
    if (!required) {
      return;
    }

    let disposed = false;
    const update = minAppVersion
      ? resolve(minAppVersion)
      : Promise.reject(new Error('Missing minimum app version.'));

    void update.then(
      (action) => {
        if (!disposed && action.status === 'store-required') {
          setResult({ minimum: minAppVersion, attempt, status: 'store' });
        }
      },
      () => {
        if (!disposed) setResult({ minimum: minAppVersion, attempt, status: 'error' });
      }
    );

    return () => {
      disposed = true;
    };
  }, [required, minAppVersion, attempt, resolve]);

  const status =
    result?.minimum === minAppVersion && result?.attempt === attempt ? result.status : 'checking';
  return { status, retry: () => setAttempt((value) => value + 1) };
}
